import {
  createYProxy,
  type DocMeta,
  type DocsPropertiesMeta,
  type WorkspaceMeta,
} from '@blocksuite/affine/store';
import { Subject } from 'rxjs';
import type * as Y from 'yjs';

type MetaState = {
  pages?: unknown[];
  properties?: DocsPropertiesMeta;
  name?: string;
  avatar?: string;
};

type IndexedDocMeta = { source: unknown; id: string; meta: DocMeta };

export class WorkspaceMetaImpl implements WorkspaceMeta {
  /* eslint-disable rxjs/finnish */
  commonFieldsUpdated = new Subject<void>();
  docMetaAdded = new Subject<string>();
  docMetaRemoved = new Subject<string>();
  docMetaUpdated = new Subject<void>();
  /* eslint-enable rxjs/finnish */

  private readonly _handleDocCollectionMetaEvents = (
    events: Y.YEvent<Y.Array<unknown> | Y.Text | Y.Map<unknown>>[]
  ) => {
    const docEvents = events.filter(
      e =>
        e.target === this.yDocs ||
        e.target.parent === this.yDocs ||
        (e.target === this._yMap && e.changes.keys.has('pages'))
    );
    const docEventSet = new Set(docEvents);
    let indexed = false;
    events.forEach(e => {
      if (docEventSet.has(e)) {
        if (!indexed) {
          this._handleDocMetaEvent(docEvents);
          indexed = true;
        }
        // Keep one update for every event that the previous observer matched.
        this.docMetaUpdated.next();
      }
      if (
        e.target === this._yMap &&
        (e.changes.keys.has('name') || e.changes.keys.has('avatar'))
      ) {
        this._handleCommonFieldsEvent();
      }
    });
  };

  private readonly _id: string = 'meta';
  private readonly _doc: Y.Doc;
  private readonly _proxy: MetaState;
  private readonly _yMap: Y.Map<MetaState[keyof MetaState]>;
  private readonly _prevDocs = new Set<string>();
  private _indexedPages: Y.Array<unknown> | undefined;
  private readonly _entries = new Map<unknown, IndexedDocMeta>();
  private readonly _entriesById = new Map<string, Set<IndexedDocMeta>>();
  private readonly _docIndex = new Map<string, IndexedDocMeta>();
  private readonly _dirtyIds = new Set<string>();

  get avatar() {
    return this._proxy.avatar;
  }

  setAvatar(avatar: string) {
    this._doc.transact(() => {
      this._proxy.avatar = avatar;
    }, this._doc.clientID);
  }

  get name() {
    return this._proxy.name;
  }

  setName(name: string) {
    this._doc.transact(() => {
      this._proxy.name = name;
    }, this._doc.clientID);
  }

  get properties(): DocsPropertiesMeta {
    const meta = this._proxy.properties;
    if (!meta) {
      return {
        tags: {
          options: [],
        },
      };
    }
    return meta;
  }

  setProperties(meta: DocsPropertiesMeta) {
    this._proxy.properties = meta;
    this.docMetaUpdated.next();
  }

  get docMetas(): DocMeta[] {
    return this.docs ?? [];
  }

  get docs(): DocMeta[] | undefined {
    // A sync transaction may replace the Y.Array itself. Resolve the current
    // array instead of retaining the previous nested proxy from the root map.
    const pages = this.yDocs;
    return pages ? createYProxy<DocMeta[]>(pages) : undefined;
  }

  get yDocs() {
    return this._yMap.get('pages') as unknown as Y.Array<unknown>;
  }

  constructor(doc: Y.Doc) {
    this._doc = doc;
    const map = doc.getMap(this._id) as Y.Map<MetaState[keyof MetaState]>;
    this._yMap = map;
    this._proxy = createYProxy(map);
    this._ensurePagesIndex();
    this._yMap.observeDeep(this._handleDocCollectionMetaEvents);
  }

  private _handleCommonFieldsEvent() {
    this.commonFieldsUpdated.next();
  }

  /** Index only an inserted entry or an entry whose identity changed. */
  private _rememberDocMeta(source: unknown) {
    const meta = createYProxy<DocMeta>(source);
    const previous = this._entries.get(source);
    if (previous?.id === meta.id) return;
    if (previous) this._forgetDocMeta(source);
    const entry = { source, id: meta.id, meta };
    this._entries.set(source, entry);
    const entries =
      this._entriesById.get(entry.id) ?? new Set<IndexedDocMeta>();
    entries.add(entry);
    this._entriesById.set(entry.id, entries);
    this._dirtyIds.add(entry.id);
    this._refreshIdIndex(entry.id);
  }

  private _forgetDocMeta(source: unknown) {
    const entry = this._entries.get(source);
    if (!entry) return;
    this._entries.delete(source);
    const entries = this._entriesById.get(entry.id)!;
    entries.delete(entry);
    if (!entries.size) this._entriesById.delete(entry.id);
    this._dirtyIds.add(entry.id);
    this._refreshIdIndex(entry.id);
  }

  private _refreshIdIndex(id: string) {
    const entries = this._entriesById.get(id);
    if (!entries?.size) {
      this._docIndex.delete(id);
    } else if (entries.size === 1) {
      this._docIndex.set(id, entries.values().next().value!);
    } else {
      // CRDT merges can retain duplicate IDs. Preserve the old first-match
      // semantics; ordinary unique-ID inserts never traverse the page list.
      for (const source of this.yDocs) {
        const entry = this._entries.get(source);
        if (entry?.id === id) {
          this._docIndex.set(id, entry);
          break;
        }
      }
    }
  }

  private _ensurePagesIndex() {
    const pages = this.yDocs;
    if (pages === this._indexedPages) return false;
    for (const id of this._entriesById.keys()) this._dirtyIds.add(id);
    this._entries.clear();
    this._entriesById.clear();
    this._docIndex.clear();
    this._indexedPages = pages;
    // Initial load or replacement of the entire pages array requires one
    // index build. Ordinary array/metadata events use the delta below.
    if (pages) for (const source of pages) this._rememberDocMeta(source);
    return true;
  }

  private _handleDocMetaEvent(
    events: Y.YEvent<Y.Array<unknown> | Y.Text | Y.Map<unknown>>[]
  ) {
    if (!this._ensurePagesIndex()) {
      for (const event of events) {
        if (event.target === this.yDocs) {
          // Deleted Y.Maps no longer expose their ID. Their retained Yjs
          // content identifies the cached entry without scanning other pages.
          for (const item of event.changes.deleted) {
            for (const source of item.content.getContent())
              this._forgetDocMeta(source);
          }
          for (const change of event.changes.delta) {
            if (Array.isArray(change.insert)) {
              for (const source of change.insert) this._rememberDocMeta(source);
            }
          }
        } else if (
          event.target.parent === this.yDocs &&
          event.changes.keys.has('id')
        ) {
          this._rememberDocMeta(event.target);
        }
      }
    }
    const added: string[] = [];
    const removed: string[] = [];
    for (const id of this._dirtyIds) {
      const exists = this._entriesById.has(id);
      if (exists && !this._prevDocs.has(id)) added.push(id);
      if (!exists && this._prevDocs.has(id)) removed.push(id);
      if (exists) this._prevDocs.add(id);
      else this._prevDocs.delete(id);
    }
    this._dirtyIds.clear();
    // Publish only the final membership of this transaction: moving/replacing
    // a page with the same ID must not remove and recreate its live document.
    for (const id of added) this.docMetaAdded.next(id);
    for (const id of removed) this.docMetaRemoved.next(id);
  }

  addDocMeta(doc: DocMeta, index?: number) {
    this._doc.transact(() => {
      if (!this.docs) {
        return;
      }
      const docs = this.docs as unknown[];
      const insertionIndex =
        index === undefined
          ? docs.length
          : Math.max(
              0,
              Math.min(docs.length, index < 0 ? docs.length + index : index)
            );
      if (index === undefined) docs.push(doc);
      else docs.splice(index, 0, doc);
      // Keep reads through this public mutator correct inside an outer Yjs
      // transaction too; the later observer updates the same index idempotently.
      this._ensurePagesIndex();
      this._rememberDocMeta(this.yDocs.get(insertionIndex));
    }, this._doc.clientID);
  }

  getDocMeta(id: string) {
    this._ensurePagesIndex();
    return this._docIndex.get(id)?.meta;
  }

  initialize() {
    if (!this.yDocs) {
      this._proxy.pages = [];
    }
  }

  removeDocMeta(id: string) {
    // you cannot delete a doc if there's no doc
    if (!this.docs) {
      return;
    }

    const docMeta = this.docMetas;
    const index = docMeta.findIndex((doc: DocMeta) => id === doc.id);
    if (index === -1) {
      return;
    }
    this._doc.transact(() => {
      if (!this.docs) {
        return;
      }
      const source = this.yDocs.get(index);
      this.docs.splice(index, 1);
      this._forgetDocMeta(source);
    }, this._doc.clientID);
  }

  setDocMeta(id: string, props: Partial<DocMeta>) {
    this._ensurePagesIndex();
    const entry = this._docIndex.get(id);
    this._doc.transact(() => {
      if (!entry || !this.docs) return;
      const doc = entry.meta as unknown as Record<string, unknown>;
      Object.entries(props).forEach(([key, value]) => {
        doc[key] = value;
      });
      this._rememberDocMeta(entry.source);
    }, this._doc.clientID);

    // Explicit emit — observeDeep on _yMap does not reliably match
    // nested Y.Map property changes inside the pages Y.Array.
    // Same pattern as setProperties() which also emits explicitly.
    if (entry) {
      this.docMetaUpdated.next();
    }
  }
}
